import React, { useState } from 'react';
import { AutoMarkingResponse } from '../../../services/autoMarking';
import StudentSummary from './StudentSummary';

interface MarkingResultsProps {
  results: AutoMarkingResponse[];
  activeIndex: number | null;
  onResultSelect: (index: number) => void;
}

const MarkingResults: React.FC<MarkingResultsProps> = ({
  results,
  activeIndex,
  onResultSelect
}) => {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [zoom, setZoom] = useState(100);

  const handlePreviewClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewIndex(index);
    setZoom(100);
  };

  const handleClosePreview = () => {
    setPreviewIndex(null);
    setZoom(100);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoom(100);
  };

  const previewResult = previewIndex !== null ? results[previewIndex] : null;

  const activeResult = activeIndex !== null ? results[activeIndex] : null;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadge = (result: AutoMarkingResponse) => {
    if (result.error) {
      return <span className="badge badge-danger">Error</span>;
    }
    if (result.success === false) {
      return <span className="badge badge-warning">Failed</span>;
    }
    return <span className="badge badge-success">Processed</span>;
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-success';
    if (percentage >= 60) return 'text-warning';
    return 'text-danger';
  };

  return (
    <div className="card marking-results">
      <div className="card-header" style={{ backgroundColor: '#17a2b8', color: '#fff', borderColor: '#17a2b8' }}>
        <h3 className="card-title text-white">
          <i className="fas fa-chart-line mr-2"></i>
          Marking Results
        </h3>
        <div className="card-tools">
          <span className="badge badge-light">{results.length} file{results.length !== 1 ? 's' : ''} processed</span>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="row no-gutters">
          {/* Results List Sidebar */}
          <div className="col-md-3 results-sidebar">
            <div className="results-list">
              <div className="list-header">
                <h6>Processed Files</h6>
              </div>
              {results.map((result, index) => (
                <div
                  key={index}
                  className={`result-item ${activeIndex === index ? 'active' : ''}`}
                  onClick={() => onResultSelect(index)}
                >
                  <div className="result-header">
                    <div className="result-name" title={result.fileName}>
                      <i className="fas fa-file-alt mr-2"></i>
                      {result.fileName}
                    </div>
                    {getStatusBadge(result)}
                  </div>
                  
                  {result.summary && (
                    <div className="result-summary">
                      <div className="question-stats">
                        <span className="stat correct">
                          <i className="fas fa-check-circle"></i> {result.summary.correctAnswers}
                        </span>
                        <span className="stat partial">
                          <i className="fas fa-exclamation-circle"></i> {result.summary.partiallyCorrect}
                        </span>
                        <span className="stat incorrect">
                          <i className="fas fa-times-circle"></i> {result.summary.incorrect}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="result-meta">
                    <small className="text-muted">
                      {formatFileSize(result.fileSize)} • {new Date(result.uploadTime).toLocaleString()}
                    </small>
                  </div>

                  {/* Preview Button */}
                  {result.imageUrl && (
                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-info btn-block"
                        onClick={(e) => handlePreviewClick(index, e)}
                      >
                        <i className="fas fa-image mr-1"></i>
                        Preview
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Result Details */}
          <div className="col-md-9 result-details">
            {activeResult ? (
              <>
                {/* Overview Content */}
                <div className="tab-content">
                  <StudentSummary result={activeResult} />
                </div>
              </>
            ) : (
              <div className="no-selection">
                <div className="text-center py-5">
                  <i className="fas fa-mouse-pointer fa-3x text-muted mb-3"></i>
                  <h5>Select a file to view results</h5>
                  <p className="text-muted">Choose a processed file from the list to see detailed marking results</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewResult && previewResult.imageUrl && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleClosePreview}
        >
          <div 
            className="modal-dialog modal-xl" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-image mr-2"></i>
                  {previewResult.fileName}
                </h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={handleClosePreview}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {/* Zoom Controls */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="text-muted">
                    <strong>Size:</strong> {formatFileSize(previewResult.fileSize)} | 
                    <strong className="ml-2">Uploaded:</strong> {new Date(previewResult.uploadTime).toLocaleString()}
                  </div>
                  <div className="zoom-controls">
                    <button 
                      className="btn btn-sm btn-outline-secondary mr-2" 
                      onClick={handleZoomOut}
                      disabled={zoom <= 50}
                    >
                      <i className="fas fa-search-minus"></i>
                    </button>
                    <span className="mx-2">{zoom}%</span>
                    <button 
                      className="btn btn-sm btn-outline-secondary ml-2 mr-2" 
                      onClick={handleZoomIn}
                      disabled={zoom >= 200}
                    >
                      <i className="fas fa-search-plus"></i>
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={handleResetZoom}
                    >
                      <i className="fas fa-redo mr-1"></i> Reset
                    </button>
                  </div>
                </div>

                {/* Image Display */}
                <div 
                  style={{ 
                    overflow: 'auto', 
                    maxHeight: '600px', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    backgroundColor: '#f8f9fa',
                    textAlign: 'center'
                  }}
                >
                  <img 
                    src={previewResult.imageUrl} 
                    alt={previewResult.fileName}
                    style={{ 
                      maxWidth: '100%',
                      height: 'auto',
                      transform: `scale(${zoom / 100})`,
                      transformOrigin: 'top center',
                      transition: 'transform 0.3s ease',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                {/* Quick Summary */}
                {previewResult.data?.summary && (
                  <div className="mt-3">
                    <div className="card">
                      <div className="card-header bg-primary text-white">
                        <h6 className="mb-0"><i className="fas fa-chart-bar mr-2"></i>Quick Summary</h6>
                      </div>
                      <div className="card-body">
                        <div className="row text-center">
                          <div className="col">
                            <h5 className="text-primary">{previewResult.data.summary.totalQuestions}</h5>
                            <p className="mb-0 small">Questions</p>
                          </div>
                          <div className="col">
                            <h5 className="text-success">{previewResult.data.summary.correctAnswers}</h5>
                            <p className="mb-0 small">Correct</p>
                          </div>
                          <div className="col">
                            <h5 className="text-warning">{previewResult.data.summary.partiallyCorrect}</h5>
                            <p className="mb-0 small">Partial</p>
                          </div>
                          <div className="col">
                            <h5 className="text-danger">{previewResult.data.summary.incorrect}</h5>
                            <p className="mb-0 small">Incorrect</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleClosePreview}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkingResults;